import React, { useState, useEffect } from 'react';
import { 
  AppBar, 
  Toolbar, 
  Typography, 
  IconButton, 
  Box,
  useMediaQuery,
  useTheme as useMuiTheme,
  Tooltip
} from '@mui/material';
import { 
  Brightness4 as DarkModeIcon, 
  Brightness7 as LightModeIcon,
  FlashOn as FlashIcon
} from '@mui/icons-material';
import { styled, alpha } from '@mui/material/styles';
import { useTheme } from '../../theme/ThemeProvider';

// Styled logo text with gradient
const LogoText = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  background: theme.palette.mode === 'dark'
    ? 'linear-gradient(45deg, #00BFFF 30%, #FF00FF 90%)'
    : 'linear-gradient(45deg, #0099CC 30%, #CC00CC 90%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
  textFillColor: 'transparent',
  letterSpacing: '0.5px',
  display: 'flex',
  alignItems: 'center',
  margin: 0, // Remove default margin for h2
}));

// Styled flash icon with animation
const AnimatedFlashIcon = styled(FlashIcon)(({ theme }) => ({
  marginRight: theme.spacing(1),
  animation: 'pulse 2s infinite',
  '@keyframes pulse': {
    '0%': {
      opacity: 0.6,
    },
    '50%': {
      opacity: 1,
      transform: 'scale(1.1)',
    },
    '100%': {
      opacity: 0.6,
    },
  },
}));

interface HeaderProps {
  onSearch: (query: string) => void;
}

const Header: React.FC<HeaderProps> = ({ onSearch }) => {
  const { mode, toggleTheme } = useTheme();
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
  const [scrolled, setScrolled] = useState(false);

  // Add scroll effect
  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 10;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [scrolled]);

  return (
    <AppBar 
      position="sticky" 
      elevation={scrolled ? 4 : 0}
      sx={{
        background: 'linear-gradient(90deg, #000000 0%, #121212 100%)',
        backdropFilter: 'blur(10px)',
        transition: 'all 0.3s ease',
        borderBottom: scrolled 
          ? 'none' 
          : `1px solid ${alpha(muiTheme.palette.common.white, 0.1)}`,
      }}
    >
      <Toolbar>
        <Box component="h2" sx={{ m: 0, display: { xs: 'none', sm: 'flex' } }}>
          <LogoText
            variant="h2"
            noWrap
            sx={{ 
              alignItems: 'center',
              fontSize: '1.75rem' // Keep the size reasonable for the header
            }}
          >
            <AnimatedFlashIcon fontSize="medium" />
            Geek Bar Pulse Flavor Finder
          </LogoText>
        </Box>
        
        <LogoText
          variant="h6"
          noWrap
          sx={{ 
            display: { xs: 'flex', sm: 'none' },
            alignItems: 'center'
          }}
        >
          <AnimatedFlashIcon fontSize="small" />
          GB Pulse
        </LogoText>
        
        <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Box 
            component="img" 
            src={`${process.env.PUBLIC_URL || ''}/images/warning.png`} 
            alt="Warning" 
            sx={{ 
              height: { xs: '30px', sm: '40px' },
              mx: 'auto',
              filter: mode === 'dark' ? 'brightness(0.9)' : 'none',
              transition: 'all 0.3s ease',
            }} 
          />
        </Box>
        
        <Tooltip title={mode === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}>
          <IconButton 
            color="inherit" 
            onClick={toggleTheme}
            aria-label={mode === 'dark' ? 'switch to light mode' : 'switch to dark mode'}
            sx={{ 
              ml: 1,
              transition: 'transform 0.3s ease',
              '&:hover': {
                transform: 'rotate(30deg)',
              }
            }}
          >
            {mode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
          </IconButton>
        </Tooltip>
      </Toolbar>
    </AppBar>
  );
};

export default Header;
